import { CancelRounded, CheckCircleRounded } from "@mui/icons-material";
import { Button, Stack, Typography } from "@mui/material";
import { observer } from "mobx-react-lite";
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  TaskProgramAssignmentFields,
  TaskProgramAssignmentValues,
  TaskProgramSettingsFields,
  TaskProgramSettingsValues,
} from "src/components/Trainings/TaskProgramFormFields";
import { ROUTE_SLUGS, ROUTES } from "src/config/navigation/routes";
import { useGetUser, useGetUsersByOrg } from "src/hooks";
import { useGetFunctionalGroups } from "src/hooks/queries";
import {
  invalidateProgramAndTasks,
  useGetTaskPrograms,
} from "src/hooks/queries/task-program-mutations";
import {
  TASK_PROGRAM_DUE_AFTER_DAYS_OPTIONS,
  TaskProgram,
  TaskProgramDueAfterDays,
  TrainingCompletionMethod,
} from "src/services/backend";
import { EntitlementType } from "src/stores/models";
import { fillRouteWithSlugs } from "src/utils";
import { hasFunctionalGroupEntitlement } from "src/utils/entitlements";
import { queryClient } from "src/utils/queryClient";
import {
  isHumanInTheLoopRejection,
  type HumanInTheLoopDecision,
} from "../../human-in-the-loop";
import { useHumanInTheLoop } from "../../HumanInTheLoopContext";
import type { ChatToolMessage } from "../../types";
import { useChatDeviceContext } from "../../useChatDeviceContext";
import { ToolCallFrame } from "./ToolCallFrame";

type DraftTrainingProgramToolProps = {
  args: Record<string, unknown>;
  toolResult?: ChatToolMessage;
  isLoading?: boolean;
  actionIndex?: number;
};

const isCompletionMethod = (
  value: unknown
): value is TrainingCompletionMethod =>
  value === "ACKNOWLEDGEMENT" || value === "QUIZ";

const isDueAfterDays = (value: unknown): value is TaskProgramDueAfterDays =>
  TASK_PROGRAM_DUE_AFTER_DAYS_OPTIONS.includes(
    value as TaskProgramDueAfterDays
  );

const getString = (value: unknown) => (typeof value === "string" ? value : "");

const getSettingsFromArgs = (
  args: Record<string, unknown>
): TaskProgramSettingsValues => ({
  name: getString(args.name),
  description: getString(args.description),
  enabled: typeof args.enabled === "boolean" ? args.enabled : true,
  completionMethod: isCompletionMethod(args.completionMethod)
    ? args.completionMethod
    : "QUIZ",
  dueAfterDays:
    args.dueAfterDays === null
      ? null
      : isDueAfterDays(args.dueAfterDays)
        ? args.dueAfterDays
        : 30,
});

const getSettingsFromProgram = (
  program: TaskProgram
): TaskProgramSettingsValues => ({
  name: program.name,
  description: program.description ?? "",
  enabled: program.enabled,
  completionMethod: program.completionMethod,
  dueAfterDays: program.dueAfterDays,
});

const getAssignmentsFromProgram = (
  program: Pick<TaskProgram, "assignments">
): TaskProgramAssignmentValues => ({
  functionalGroupIds: program.assignments.flatMap((assignment) =>
    assignment.functionalGroupId ? [assignment.functionalGroupId] : []
  ),
  userIds: program.assignments.flatMap((assignment) =>
    assignment.userId ? [assignment.userId] : []
  ),
});

const emptyAssignments: TaskProgramAssignmentValues = {
  functionalGroupIds: [],
  userIds: [],
};

const getAssignmentsFromArgs = (
  value: unknown
): TaskProgramAssignmentValues => {
  if (!Array.isArray(value)) return emptyAssignments;

  return value.reduce<TaskProgramAssignmentValues>(
    (assignments, value) => {
      if (!value || typeof value !== "object") return assignments;
      const assignment = value as Record<string, unknown>;
      if (typeof assignment.functionalGroupId === "string") {
        assignments.functionalGroupIds.push(assignment.functionalGroupId);
      } else if (typeof assignment.userId === "string") {
        assignments.userIds.push(assignment.userId);
      }
      return assignments;
    },
    { functionalGroupIds: [], userIds: [] }
  );
};

const parseCreatedProgram = (content: unknown): TaskProgram | null => {
  let parsed = content;
  if (typeof content === "string") {
    try {
      parsed = JSON.parse(content);
    } catch {
      return null;
    }
  }
  if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) {
    return null;
  }

  const program = parsed as Partial<TaskProgram>;
  return typeof program.id === "string" &&
    typeof program.linkedDocumentId === "string" &&
    typeof program.name === "string" &&
    typeof program.enabled === "boolean" &&
    isCompletionMethod(program.completionMethod) &&
    Array.isArray(program.assignments)
    ? (program as TaskProgram)
    : null;
};

const TRAINING_PERMISSION_ERROR =
  "You do not have permission to edit this training.";

const DraftTrainingProgramToolComponent = ({
  args,
  toolResult,
  isLoading = false,
  actionIndex,
}: DraftTrainingProgramToolProps) => {
  const { orgId } = useChatDeviceContext();
  const navigate = useNavigate();
  const humanInTheLoop = useHumanInTheLoop();
  const { data: user, isLoading: userLoading } = useGetUser();
  const canAdministerAllTrainings =
    !!user &&
    (user.permissions?.includes("write:configuration") ||
      hasFunctionalGroupEntitlement(
        user,
        orgId,
        EntitlementType.ADMINISTER_ALL_TRAININGS
      ));
  const { data: programs = [] } = useGetTaskPrograms(orgId, "all", {
    enabled: !!orgId && canAdministerAllTrainings,
  });
  const { data: functionalGroups = [] } = useGetFunctionalGroups(orgId, {
    enabled: canAdministerAllTrainings,
  });
  const { data: orgUsers = [], isLoading: usersLoading } = useGetUsersByOrg({
    orgId,
    enabled: canAdministerAllTrainings,
  });
  const documentId = getString(args.documentId);
  const documentVersionId = getString(args.documentVersionId);
  const documentTitle = getString(args.documentTitle);
  const revision =
    typeof args.revision === "number" && Number.isInteger(args.revision)
      ? args.revision
      : null;
  const existingProgram = programs.find(
    (program) => program.enabled && program.linkedDocumentId === documentId
  );
  const createdProgram = useMemo(
    () => parseCreatedProgram(toolResult?.content),
    [toolResult?.content]
  );
  const pendingActionIndex =
    actionIndex ??
    (humanInTheLoop?.request?.action_requests.length === 1 ? 0 : undefined);
  const isPendingReview = pendingActionIndex !== undefined && !toolResult;
  const hasDecision =
    pendingActionIndex !== undefined &&
    !!humanInTheLoop?.decisions[pendingActionIndex];
  const displayedProgram = toolResult
    ? (createdProgram ?? existingProgram)
    : undefined;
  const initialSettings = useMemo(
    () =>
      displayedProgram
        ? getSettingsFromProgram(displayedProgram)
        : getSettingsFromArgs(args),
    [args, displayedProgram]
  );
  const initialAssignments = useMemo(
    () =>
      displayedProgram
        ? getAssignmentsFromProgram(displayedProgram)
        : getAssignmentsFromArgs(args.assignments),
    [args.assignments, displayedProgram]
  );
  const [settings, setSettings] =
    useState<TaskProgramSettingsValues>(initialSettings);
  const [assignments, setAssignments] =
    useState<TaskProgramAssignmentValues>(initialAssignments);
  const [error, setError] = useState<string | null>(null);
  const invalidatedSuccessfulResult = useRef(false);
  const stateOwnerKey = toolResult?.tool_call_id ?? "pending";
  const stateOwnerKeyRef = useRef(stateOwnerKey);
  const program = displayedProgram;

  useEffect(() => {
    if (stateOwnerKeyRef.current === stateOwnerKey) return;
    stateOwnerKeyRef.current = stateOwnerKey;
    setSettings(initialSettings);
    setAssignments(initialAssignments);
    setError(null);
  }, [initialAssignments, initialSettings, stateOwnerKey]);

  useEffect(() => {
    if (toolResult?.status !== "success") {
      invalidatedSuccessfulResult.current = false;
      return;
    }
    if (!toolResult || invalidatedSuccessfulResult.current) return;

    invalidatedSuccessfulResult.current = true;
    invalidateProgramAndTasks(queryClient, orgId, createdProgram?.id);
  }, [createdProgram?.id, orgId, toolResult, toolResult?.status]);

  const contextError =
    !documentId || !documentVersionId || !documentTitle || revision == null
      ? "This proposal is missing the document revision context."
      : null;
  const hasTargets =
    assignments.functionalGroupIds.length > 0 || assignments.userIds.length > 0;
  const validationError =
    contextError ??
    (isPendingReview && existingProgram
      ? "A training program already exists for this document."
      : null) ??
    (!settings.name.trim()
      ? "Name is required."
      : !hasTargets
        ? "Select at least one functional group or individual user."
        : null);
  const isRejected = isHumanInTheLoopRejection(toolResult);
  const hasToolError = toolResult?.status === "error" && !isRejected;
  const isLocked =
    !isPendingReview || hasDecision || isLoading || humanInTheLoop?.isLoading;

  const submitDecision = (decision: HumanInTheLoopDecision) => {
    if (
      !humanInTheLoop?.request ||
      pendingActionIndex === undefined ||
      !isPendingReview
    ) {
      setError(
        "This training program review is no longer active. Reload the conversation."
      );
      return;
    }

    humanInTheLoop.setDecision(pendingActionIndex, decision);
  };

  const handleCreate = () => {
    if (!canAdministerAllTrainings) {
      setError(TRAINING_PERMISSION_ERROR);
      return;
    }
    if (validationError || hasToolError) {
      setError(validationError ?? "Could not create this training program.");
      return;
    }

    setError(null);
    submitDecision({
      type: "edit",
      edited_action: {
        name: "draft_training_program",
        args: {
          ...args,
          documentId,
          documentVersionId,
          revision,
          documentTitle,
          name: settings.name.trim(),
          description: settings.description.trim(),
          enabled: settings.enabled,
          completionMethod: settings.completionMethod,
          dueAfterDays: settings.dueAfterDays,
          assignments: [
            ...assignments.functionalGroupIds.map((functionalGroupId) => ({
              functionalGroupId,
            })),
            ...assignments.userIds.map((userId) => ({ userId })),
          ],
        },
      },
    });
  };

  const editTraining = () => {
    if (!canAdministerAllTrainings || !program) return;
    navigate(
      fillRouteWithSlugs(ROUTES.TRAINING_EDIT, {
        [ROUTE_SLUGS.ORG_ID]: orgId,
        [ROUTE_SLUGS.PROGRAM_ID]: program.id,
      })
    );
  };

  const actionPending = userLoading || usersLoading || isLocked;

  const actions =
    !canAdministerAllTrainings ||
    hasToolError ||
    isRejected ? null : isPendingReview ? (
      <div className="flex shrink-0 items-center gap-2 whitespace-nowrap">
        <Button
          variant="contained"
          color="error"
          sx={{ backgroundColor: "#d32f2f" }}
          size="small"
          startIcon={<CancelRounded />}
          onClick={() => submitDecision({ type: "reject" })}
          disabled={actionPending}
        >
          Discard
        </Button>
        <Button
          variant="contained"
          color="success"
          sx={{ backgroundColor: "#15803d" }}
          size="small"
          startIcon={<CheckCircleRounded />}
          onClick={handleCreate}
          disabled={actionPending || !!validationError}
        >
          Create training
        </Button>
      </div>
    ) : program ? (
      <Button variant="outlined" size="small" onClick={editTraining}>
        Edit training
      </Button>
    ) : null;

  return (
    <ToolCallFrame
      title={
        documentTitle
          ? `Training program proposal — ${documentTitle}`
          : "Training program proposal"
      }
      isLoading={userLoading || isLoading}
      isRejected={isRejected}
      hasError={
        !isRejected &&
        ((!userLoading && !canAdministerAllTrainings) ||
          hasToolError ||
          !!error ||
          !!contextError)
      }
      expandedByDefault={!isRejected}
      hasToolResult={!isRejected && !!toolResult}
      hasContent={!isRejected && !!documentId}
      actions={isPendingReview ? undefined : actions}
      footer={isPendingReview ? actions : undefined}
    >
      {!userLoading && !canAdministerAllTrainings ? (
        <Typography color="error" variant="body2">
          {TRAINING_PERMISSION_ERROR}
        </Typography>
      ) : (
        <Stack spacing={3}>
          {hasToolError && (
            <Typography color="error" variant="body2">
              {typeof toolResult?.content === "string"
                ? toolResult.content
                : "Could not draft this training program."}
            </Typography>
          )}

          <TaskProgramSettingsFields
            linkedDocumentTitle={documentTitle}
            values={settings}
            onChange={setSettings}
            completionMethodHelperText="Quiz tasks are sent after the quiz for that revision is published."
            disabled={isLocked}
            enabledInputId={`training-draft-enabled-${toolResult?.tool_call_id ?? "pending"}`}
          />

          <TaskProgramAssignmentFields
            values={assignments}
            onChange={setAssignments}
            functionalGroups={functionalGroups}
            orgUsers={orgUsers}
            usersLoading={usersLoading}
            disabled={isLocked}
          />

          {isPendingReview && validationError && (
            <Typography color="text.secondary" variant="caption">
              {validationError}
            </Typography>
          )}
          {toolResult?.status === "success" && (
            <Typography color="success.main" variant="body2">
              Training program created.
            </Typography>
          )}
          {error && (
            <Typography color="error" variant="body2">
              {error}
            </Typography>
          )}
        </Stack>
      )}
    </ToolCallFrame>
  );
};

const DraftTrainingProgramTool = observer(DraftTrainingProgramToolComponent);

export default DraftTrainingProgramTool;
